import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Bot,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  useAdsWorkspaceStore,
  useAdsResearchStore,
  persistAdArtifact,
  isSyntheticLocalBananJob,
  type SubAgentId,
  type BananImageResult,
  type KlingVideoResult,
} from "@/features/ads";
import { ADS_SUB_AGENTS, type CompanionSubAgent } from "../../lib/chatAgentRegistry";
import { RunningAgentMiniCard, type MiniCardResult } from "./RunningAgentMiniCard";

// ── Types ─────────────────────────────────────────────────────────────────

interface DismissingEntry {
  jobId: string;
  source: "banan" | "kling" | "higgsfield";
  dispatchedAt: number;
  finalStatus: "completed" | "failed";
  dismissedAt: number;
}

const DISMISS_DELAY_MS = 5_000;
const RUNTIME_SESSION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ── Agent Data ────────────────────────────────────────────────────────────

export type SubAgent = CompanionSubAgent;

// ── SubAgent Avatar Component ─────────────────────────────────────────────

function SubAgentAvatar({
  agent,
  isActive,
  selected,
  jobCount,
  onClick,
}: {
  agent: SubAgent;
  isActive: boolean;
  /** Whether this sub-agent's chat is currently shown in the side panel. */
  selected: boolean;
  jobCount: number;
  onClick?: () => void;
}) {
  const Icon = agent.icon;

  return (
    <div className="flex flex-col items-center gap-1.5 cursor-pointer group" onClick={onClick}>
      <motion.div
        className={cn(
          "relative flex items-center justify-center w-10 h-10 rounded-2xl shadow-sm transition-all duration-300",
          selected
            ? "bg-surface-high ring-2 shadow-md"
            : isActive
              ? "bg-surface-high ring-2 ring-primary/50"
              : "bg-surface-container hover:bg-surface-high group-hover:scale-105",
        )}
        style={selected ? { ["--tw-ring-color" as string]: agent.bgHex } : undefined}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Glow behind when active */}
        {isActive && (
          <motion.div
            className="absolute inset-0 rounded-2xl opacity-20 blur-md pointer-events-none"
            style={{ backgroundColor: agent.bgHex }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            exit={{ opacity: 0 }}
          />
        )}

        <div className={cn("relative z-10", agent.color, isActive ? "drop-shadow-md" : "")}>
          <Icon className={cn("w-5 h-5", isActive ? "opacity-100" : "opacity-70")} />

          {/* Nano Banan - Image Agent */}
          {agent.id === "img-agent" && (
            <>
              <div className="absolute top-[3px] right-[3px] w-1.5 h-1.5 bg-red-500 rounded-full border border-surface-high shadow-[0_0_4px_#ef4444]" />
              <div className="absolute -top-1 left-1/2 w-0.5 h-1 bg-yellow-600/80 rounded-t-full -translate-x-1/2" />
            </>
          )}

          {/* CineBot - Video Agent */}
          {agent.id === "vid-agent" && (
            <>
              <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_4px_#ef4444]" />
              <div className="absolute top-[6px] left-[7px] w-1.5 h-1.5 bg-cyan-300 rounded-full shadow-[0_0_4px_#67e8f9] opacity-90" />
              <div className="absolute top-1 -left-1 w-1.5 h-1.5 border-[1.5px] border-blue-500/80 rounded-full" />
            </>
          )}

          {/* Scout - Research Agent */}
          {agent.id === "res-agent" && (
            <>
              <div className="absolute top-[4px] left-[4px] w-2 h-2 bg-purple-500/40 rounded-full shadow-[0_0_6px_#a855f7] flex items-center justify-center">
                <div className="w-0.5 h-0.5 bg-white rounded-full" />
              </div>
              <div className="absolute top-[3px] -right-[2px] w-1.5 h-1 bg-purple-400/80 rounded-r-full" />
              <div className="absolute bottom-[2px] right-[5px] w-1 h-1 bg-green-400 rounded-full shadow-[0_0_4px_#4ade80] animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
            </>
          )}
        </div>

        {/* Active badge/pulse — driven by real job count */}
        {isActive && (
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5">
            <span
              className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping"
              style={{ backgroundColor: agent.bgHex }}
            />
            <span
              className="relative inline-flex w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: agent.bgHex }}
            />
          </div>
        )}

        {/* Job count badge */}
        {jobCount > 0 && (
          <span className="absolute -bottom-1 -right-1 flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full bg-primary/90 ds-micro-strong text-white tabular-nums shadow-sm">
            {jobCount}
          </span>
        )}

        {/* Chat-active indicator */}
        {selected && (
          <div className="absolute -bottom-1 -left-1 flex items-center justify-center w-4 h-4 rounded-full bg-background shadow-sm">
            <MessageCircle className="w-2.5 h-2.5" style={{ color: agent.bgHex }} />
          </div>
        )}
      </motion.div>
      <div className="flex flex-col items-center">
        <span
          className={cn(
            "ds-micro leading-none mb-0.5 transition-colors",
            selected ? "text-foreground font-bold" : isActive ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {agent.name}
        </span>
        <span className={cn(
          "ds-micro-strong uppercase",
          selected ? "text-foreground/60" : "text-muted-foreground/60",
        )}>
          {selected ? "Chatting" : agent.role}
        </span>
      </div>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────

export function RunningAgentsPanel() {
  const pendingKlingJobs = useAdsWorkspaceStore((s) => s.pendingKlingJobs);
  const pendingBananJobs = useAdsWorkspaceStore((s) => s.pendingBananJobs);
  const pendingHiggsfieldJobs = useAdsWorkspaceStore((s) => s.pendingHiggsfieldJobs);
  // Synthetic local-Banan start-image jobs (jobId `local-banan-*`) resolve
  // synchronously via the Django start-image call and have NO managed-chat
  // status record. Rendering a mini-card for one polls that endpoint forever,
  // 404ing every 5s and freezing a "Generation…" card that never resolves —
  // the UGC video-builder bug the user hit. Their lifecycle is owned by
  // `dispatchUgcStartImageLocal` (added on start, removed in its `finally`)
  // and the result-panel loading tile already reflects their progress, so the
  // sub-agent panel must never render or poll them. Mirrors the guard in
  // `useAdJobsPoller`.
  const trackedBananJobs = pendingBananJobs.filter(
    (j) => !isSyntheticLocalBananJob(j.jobId),
  );
  const activeSubAgent = useAdsWorkspaceStore((s) => s.activeSubAgent);
  const setActiveSubAgent = useAdsWorkspaceStore((s) => s.setActiveSubAgent);
  const revealedSubAgents = useAdsWorkspaceStore((s) => s.revealedSubAgents);
  const researchStatus = useAdsResearchStore((s) => s.status);
  const [dismissing, setDismissing] = useState<DismissingEntry[]>([]);
  const dismissingRef = useRef(dismissing);
  dismissingRef.current = dismissing;

  // Track research dismiss — Scout stays active for a short grace period
  // after research completes/errors so the user sees the final state.
  const [researchDismissed, setResearchDismissed] = useState(false);
  const prevResearchStatusRef = useRef(researchStatus);

  useEffect(() => {
    const prev = prevResearchStatusRef.current;
    prevResearchStatusRef.current = researchStatus;

    // When research transitions to complete/error, start dismiss timer
    if (
      (researchStatus === "complete" || researchStatus === "error") &&
      prev === "running"
    ) {
      setResearchDismissed(false);
      const timer = setTimeout(() => setResearchDismissed(true), DISMISS_DELAY_MS);
      return () => clearTimeout(timer);
    }

    // When research starts again, reset dismiss
    if (researchStatus === "running") {
      setResearchDismissed(false);
    }
  }, [researchStatus]);

  // Clean up expired dismissing entries
  useEffect(() => {
    if (dismissing.length === 0) return;
    const id = setInterval(() => {
      const now = Date.now();
      setDismissing((prev) => prev.filter((d) => now - d.dismissedAt < DISMISS_DELAY_MS));
    }, 1_000);
    return () => clearInterval(id);
  }, [dismissing.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Kling completion handlers ──
  const handleKlingComplete = useCallback((jobId: string, result: MiniCardResult) => {
    const store = useAdsWorkspaceStore.getState();
    const job = store.pendingKlingJobs.find((j) => j.jobId === jobId);
    if (job?.provider === "heygen") {
      // HeyGen adclone jobs need the ads poller path because it persists the
      // provider IDs and publishes the clone-final-video-ready editor event.
      return;
    }

    // Batch job: the agent emitted a videos[] array in the same order as the
    // job's batchItems. Each successful entry becomes its own GeneratedAd.
    const isBatch = !!(job?.batchItems && (result.videos?.length ?? 0) > 0);

    store.completeKlingJob(jobId, result.videoUrl, result.thumbnailUrl, result.videos);
    const anySuccess =
      !!result.videoUrl ||
      !!(result.videos && result.videos.some((v) => v.videoUrl));
    if (anySuccess) store.setActiveTab("assets");

    // Persist artifacts: batch → one per successful video with per-item lineage,
    // otherwise single-video fallback (no artifact persistence today — matches
    // the previous behavior which relied on the coagent pipeline).
    if (isBatch && job && result.videos) {
      const workspaceId = store.scopeWorkspaceId;
      const successCount = result.videos.filter((v) => v.videoUrl).length;
      const failureCount = result.videos.length - successCount;
      toast.success("Video generation complete", {
        description: failureCount > 0
          ? `${successCount} of ${result.videos.length} videos ready in the Assets tab (${failureCount} failed).`
          : `${successCount} videos are ready in the Assets tab.`,
      });

      if (workspaceId) {
        result.videos.forEach((v, i) => {
          if (!v.videoUrl) return;
          const lineage = job.batchItems?.[i];
          const videoResult: KlingVideoResult = {
            id: `${jobId}_${i}`,
            status: "done",
            video_output: v.videoUrl,
            video_thumbnail: v.thumbnailUrl,
          };
          void persistAdArtifact({
            adType: "video",
            result: videoResult,
            workspaceId,
            label: "Agent Vid",
            extraMetadata: {
              source: "vid",
              jobId,
              batchIndex: i,
              ...(job.productId ? { productId: job.productId } : {}),
              ...(job.productName ? { productName: job.productName } : {}),
              ...(lineage?.mode ? { klingMode: lineage.mode } : {}),
              ...(lineage?.sourceImageJobId ? { sourceImageJobId: lineage.sourceImageJobId } : {}),
              ...(lineage?.sourceImageUrl ? { sourceImageUrl: lineage.sourceImageUrl } : {}),
              ...(lineage?.sourceResearchAngle ? { sourceResearchAngle: lineage.sourceResearchAngle } : {}),
              ...(lineage?.sourceVideoScriptAngle ? { sourceVideoScriptAngle: lineage.sourceVideoScriptAngle } : {}),
              ...(lineage?.targetProblem ? { targetProblem: lineage.targetProblem } : {}),
            },
          });
        });
      }
    }

    setDismissing((prev) => {
      if (prev.some((d) => d.jobId === jobId)) return prev;
      return [
        ...prev,
        {
          jobId,
          source: "kling",
          dispatchedAt: job?.dispatchedAt ?? Date.now(),
          finalStatus: "completed",
          dismissedAt: Date.now(),
        },
      ];
    });
  }, []);

  const handleKlingFailed = useCallback((jobId: string) => {
    const store = useAdsWorkspaceStore.getState();
    const job = store.pendingKlingJobs.find((j) => j.jobId === jobId);
    store.removeKlingJob(jobId);
    setDismissing((prev) => {
      if (prev.some((d) => d.jobId === jobId)) return prev;
      return [
        ...prev,
        {
          jobId,
          source: "kling",
          dispatchedAt: job?.dispatchedAt ?? Date.now(),
          finalStatus: "failed",
          dismissedAt: Date.now(),
        },
      ];
    });
  }, []);

  // ── Banan completion handlers ��─
  const handleBananComplete = useCallback((jobId: string, result: MiniCardResult) => {
    const store = useAdsWorkspaceStore.getState();
    const job = store.pendingBananJobs.find((j) => j.jobId === jobId);
    store.completeBananJob(jobId, result.imageUrl, result.imageUrls);
    if (result.imageUrl || result.imageUrls?.length) store.setActiveTab("assets");

    // Persist artifact + toast when we're the first completion path to fire
    // (job still present in pending means ChatMessageList hasn't handled it yet)
    if (job) {
      // Treat any multi-URL result as a batch for persistence so the legacy
      // multi-variant path (`dispatchImageAd` with `imageCount > 1`, no
      // batchItems) writes one artifact per image instead of dropping N-1
      // and forcing a chat-history rebuild to recover them.
      const isBatch = (result.imageUrls?.length ?? 0) > 1;

      toast.success("Image generation complete", {
        description: isBatch
          ? `${result.imageUrls!.length} images are ready in the Assets tab.`
          : job.productName
            ? `Image for "${job.productName}" is ready in Assets.`
            : "Your image ad is ready in the Assets tab.",
      });

      const workspaceId = store.scopeWorkspaceId;
      if (workspaceId && (result.imageUrl || result.imageUrls?.length)) {
        // Batch: persist each image as its own artifact with per-item lineage
        if (isBatch && result.imageUrls) {
          for (let i = 0; i < result.imageUrls.length; i++) {
            const lineage = job.batchItems?.[i];
            const imageResult: BananImageResult = {
              id: `${jobId}_${i}`,
              status: "done",
              output: result.imageUrls[i],
            };
            void persistAdArtifact({
              adType: "image",
              result: imageResult,
              workspaceId,
              label: "Banan AI",
              extraMetadata: {
                source: "banan",
                jobId,
                batchIndex: i,
                ...(job.productId ? { productId: job.productId } : {}),
                ...(job.productName ? { productName: job.productName } : {}),
                ...(lineage?.sourceResearchAngle ? { sourceResearchAngle: lineage.sourceResearchAngle } : {}),
                ...(lineage?.targetProblem ? { targetProblem: lineage.targetProblem } : {}),
                ...(lineage?.overlayText ? { overlayText: lineage.overlayText } : {}),
                ...(lineage?.ugcSceneSlot ? { ugcSceneSlot: lineage.ugcSceneSlot } : {}),
                ...(lineage?.ugcSceneIndex !== undefined ? { ugcSceneIndex: lineage.ugcSceneIndex } : {}),
                ...(lineage?.ugcVideoIndex !== undefined ? { ugcVideoIndex: lineage.ugcVideoIndex } : {}),
                ...(lineage?.sourceVideoScriptAngle ? { sourceVideoScriptAngle: lineage.sourceVideoScriptAngle } : {}),
              },
            });
          }
        } else {
          // Single image: original behavior
          const imageResult: BananImageResult = {
            id: jobId,
            status: "done",
            output: result.imageUrl ?? result.imageUrls?.[0],
          };
          void persistAdArtifact({
            adType: "image",
            result: imageResult,
            workspaceId,
            label: "Banan AI",
            extraMetadata: {
              source: "banan",
              jobId,
              ...(job.productId ? { productId: job.productId } : {}),
              ...(job.productName ? { productName: job.productName } : {}),
              ...(job.sourceResearchAngle ? { sourceResearchAngle: job.sourceResearchAngle } : {}),
              ...(job.targetProblem ? { targetProblem: job.targetProblem } : {}),
              ...(job.overlayText ? { overlayText: job.overlayText } : {}),
            },
          });
        }
      }
    }

    setDismissing((prev) => {
      if (prev.some((d) => d.jobId === jobId)) return prev;
      return [
        ...prev,
        {
          jobId,
          source: "banan",
          dispatchedAt: job?.dispatchedAt ?? Date.now(),
          finalStatus: "completed",
          dismissedAt: Date.now(),
        },
      ];
    });
  }, []);

  const handleRuntimeBananComplete = useCallback((jobId: string) => {
    useAdsWorkspaceStore.getState().removeBananJob(jobId);
  }, []);

  const handleBananFailed = useCallback((jobId: string) => {
    const store = useAdsWorkspaceStore.getState();
    const job = store.pendingBananJobs.find((j) => j.jobId === jobId);
    store.removeBananJob(jobId);
    setDismissing((prev) => {
      if (prev.some((d) => d.jobId === jobId)) return prev;
      return [
        ...prev,
        {
          jobId,
          source: "banan",
          dispatchedAt: job?.dispatchedAt ?? Date.now(),
          finalStatus: "failed",
          dismissedAt: Date.now(),
        },
      ];
    });
  }, []);

  // ── Higgsfield completion handlers ──
  // Mirrors the Kling batch path: completeHiggsfieldJob persists each successful
  // video as its own GeneratedAd via the store, and we additionally write
  // backend artifacts so they survive a reload (the Higgsfield modal-fire path
  // doesn't go through Marcus, so the coagent pipeline can't persist them).
  const handleHiggsfieldComplete = useCallback((jobId: string, result: MiniCardResult) => {
    const store = useAdsWorkspaceStore.getState();
    const job = store.pendingHiggsfieldJobs.find((j) => j.jobId === jobId);
    const isBatch = !!(job?.batchItems && (result.videos?.length ?? 0) > 0);

    store.completeHiggsfieldJob(jobId, result.videoUrl, result.thumbnailUrl, result.videos);
    const anySuccess =
      !!result.videoUrl ||
      !!(result.videos && result.videos.some((v) => v.videoUrl));
    if (anySuccess) store.setActiveTab("assets");

    if (job) {
      const workspaceId = store.scopeWorkspaceId;

      if (isBatch && result.videos) {
        const successCount = result.videos.filter((v) => v.videoUrl).length;
        const failureCount = result.videos.length - successCount;
        toast.success("Video generation complete", {
          description: failureCount > 0
            ? `${successCount} of ${result.videos.length} videos ready in the Assets tab (${failureCount} failed).`
            : `${successCount} videos are ready in the Assets tab.`,
        });

        if (workspaceId) {
          result.videos.forEach((v, i) => {
            if (!v.videoUrl) return;
            const lineage = job.batchItems?.[i];
            const videoResult: KlingVideoResult = {
              id: `${jobId}_${i}`,
              status: "done",
              video_output: v.videoUrl,
              video_thumbnail: v.thumbnailUrl,
            };
            void persistAdArtifact({
              adType: "video",
              result: videoResult,
              workspaceId,
              label:
                job.generator === "higgsfield_cli" ? "Agent Higgs" : "Agent Vidzz",
              extraMetadata: {
                source: job.generator ?? "higgsfield",
                generator: job.generator ?? "higgsfield",
                jobId,
                batchIndex: i,
                ...(job.productId ? { productId: job.productId } : {}),
                ...(job.productName ? { productName: job.productName } : {}),
                ...(lineage?.videoTypeId ? { videoTypeId: lineage.videoTypeId } : {}),
                ...(lineage?.higgsfieldMode ? { higgsfieldMode: lineage.higgsfieldMode } : {}),
                ...(lineage?.variantAngle ? { variantAngle: lineage.variantAngle } : {}),
                ...(lineage?.aspectRatio ? { aspectRatio: lineage.aspectRatio } : {}),
                ...(lineage?.duration ? { duration: lineage.duration } : {}),
              },
            });
          });
        }
      } else if (result.videoUrl) {
        toast.success("Video generation complete", {
          description: job.productName
            ? `Video for "${job.productName}" is ready in Assets.`
            : "Your video ad is ready in the Assets tab.",
        });
        if (workspaceId) {
          const videoResult: KlingVideoResult = {
            id: jobId,
            status: "done",
            video_output: result.videoUrl,
            video_thumbnail: result.thumbnailUrl,
          };
          void persistAdArtifact({
            adType: "video",
            result: videoResult,
            workspaceId,
            label:
              job.generator === "higgsfield_cli" ? "Agent Higgs" : "Agent Vidzz",
            extraMetadata: {
              source: job.generator ?? "higgsfield",
              generator: job.generator ?? "higgsfield",
              jobId,
              ...(job.productId ? { productId: job.productId } : {}),
              ...(job.productName ? { productName: job.productName } : {}),
              ...(job.videoTypeId ? { videoTypeId: job.videoTypeId } : {}),
              ...(job.higgsfieldMode ? { higgsfieldMode: job.higgsfieldMode } : {}),
            },
          });
        }
      }
    }

    setDismissing((prev) => {
      if (prev.some((d) => d.jobId === jobId)) return prev;
      return [
        ...prev,
        {
          jobId,
          source: "higgsfield",
          dispatchedAt: job?.dispatchedAt ?? Date.now(),
          finalStatus: "completed",
          dismissedAt: Date.now(),
        },
      ];
    });
  }, []);

  const handleHiggsfieldFailed = useCallback((jobId: string) => {
    const store = useAdsWorkspaceStore.getState();
    const job = store.pendingHiggsfieldJobs.find((j) => j.jobId === jobId);
    store.removeHiggsfieldJob(jobId);
    setDismissing((prev) => {
      if (prev.some((d) => d.jobId === jobId)) return prev;
      return [
        ...prev,
        {
          jobId,
          source: "higgsfield",
          dispatchedAt: job?.dispatchedAt ?? Date.now(),
          finalStatus: "failed",
          dismissedAt: Date.now(),
        },
      ];
    });
  }, []);

  // Filter out any dismissing entries that also appear in active jobs (race guard)
  const pendingIds = new Set([
    ...pendingKlingJobs.map((j) => j.jobId),
    ...trackedBananJobs.map((j) => j.jobId),
    ...pendingHiggsfieldJobs.map((j) => j.jobId),
  ]);
  const activeDismissing = dismissing.filter((d) => !pendingIds.has(d.jobId));

  // Scout is active while research is running, or during the post-run grace period.
  const showResearch =
    researchStatus === "running" ||
    ((researchStatus === "complete" || researchStatus === "error") && !researchDismissed);

  const pendingVidJobs = pendingKlingJobs.filter((j) => j.provider !== "heygen");
  const pendingHeyGenJobs = pendingKlingJobs.filter((j) => j.provider === "heygen");

  const totalActive =
    pendingKlingJobs.length +
    trackedBananJobs.length +
    activeDismissing.length +
    (showResearch ? 1 : 0);

  // Drive avatar active state from real jobs
  const bananActive = trackedBananJobs.length > 0;
  const vidActive = pendingVidJobs.length > 0;
  const agentVActive = pendingHeyGenJobs.length > 0;
  const higgsfieldActive = pendingHiggsfieldJobs.some(
    (j) => j.generator !== "higgsfield_cli",
  );
  const higgsfieldCliActive = pendingHiggsfieldJobs.some(
    (j) => j.generator === "higgsfield_cli",
  );

  function jobCountForAgent(agentId: SubAgentId): number {
    if (agentId === "banan") {
      // Show total image count across all jobs (batch jobs contribute itemCount)
      return trackedBananJobs.reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (agentId === "vid") {
      // Show total video count across all jobs (batch jobs contribute itemCount)
      return pendingVidJobs.reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (agentId === "agent_v") {
      return pendingHeyGenJobs.reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (agentId === "higgsfield") {
      return pendingHiggsfieldJobs
        .filter((j) => j.generator !== "higgsfield_cli")
        .reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (agentId === "higgsfield_cli") {
      return pendingHiggsfieldJobs
        .filter((j) => j.generator === "higgsfield_cli")
        .reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (agentId === "scout") return showResearch ? 1 : 0;
    return 0;
  }

  function isAgentActive(agentId: SubAgentId): boolean {
    if (agentId === "banan") return bananActive;
    if (agentId === "vid") return vidActive;
    if (agentId === "agent_v") return agentVActive;
    if (agentId === "higgsfield") return higgsfieldActive;
    if (agentId === "higgsfield_cli") return higgsfieldCliActive;
    if (agentId === "scout") return showResearch;
    return false;
  }

  // Only show agents that have been activated (dispatched job, research started, etc.)
  const visibleAgents = ADS_SUB_AGENTS.filter((a) => revealedSubAgents.includes(a.subAgentId));

  // Hide entire panel until at least one sub-agent is revealed
  if (visibleAgents.length === 0) return null;

  return (
    <div className="px-4 py-3 shrink-0 border-y border-border/20 bg-muted/5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Bot className="w-3.5 h-3.5 text-primary/70" />
          <span className="ds-micro-strong uppercase text-foreground/70">
            Sub-Agents
          </span>
        </div>
        {totalActive > 0 && (
          <div className="ds-micro flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary/80">
            <Sparkles className="w-2.5 h-2.5" />
            <span>{totalActive} Running</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-around mt-1 relative">
        {visibleAgents.map((agent) => (
          <SubAgentAvatar
            key={agent.id}
            agent={agent}
            isActive={isAgentActive(agent.subAgentId)}
            selected={activeSubAgent === agent.subAgentId}
            jobCount={jobCountForAgent(agent.subAgentId)}
            onClick={() => {
              // Scout: redirect to research tab instead of opening chat
              if (agent.subAgentId === "scout") {
                useAdsWorkspaceStore.getState().setActiveTab("research");
                return;
              }
              // Toggle: clicking the already-selected agent deselects (back to Marcus)
              if (activeSubAgent === agent.subAgentId) {
                setActiveSubAgent(null);
              } else {
                setActiveSubAgent(agent.subAgentId);
              }
            }}
          />
        ))}
      </div>

      {/* Active job cards */}
      <AnimatePresence>
        {(trackedBananJobs.length > 0 ||
          pendingKlingJobs.length > 0 ||
          pendingHiggsfieldJobs.length > 0 ||
          activeDismissing.length > 0) && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 10 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-1">
              {trackedBananJobs.map((job) => (
                <RunningAgentMiniCard
                  key={job.jobId}
                  jobId={job.jobId}
                  dispatchedAt={job.dispatchedAt}
                  statusEndpoint={
                    RUNTIME_SESSION_ID_RE.test(job.jobId)
                      ? "runtime-session-image"
                      : "managed-agent"
                  }
                  onComplete={handleBananComplete}
                  onRuntimeComplete={handleRuntimeBananComplete}
                  onFailed={handleBananFailed}
                />
              ))}
              {pendingKlingJobs.map((job) => (
                <RunningAgentMiniCard
                  key={job.jobId}
                  jobId={job.jobId}
                  dispatchedAt={job.dispatchedAt}
                  onComplete={handleKlingComplete}
                  onFailed={handleKlingFailed}
                />
              ))}
              {pendingHiggsfieldJobs.map((job) => (
                <RunningAgentMiniCard
                  key={job.jobId}
                  jobId={job.jobId}
                  dispatchedAt={job.dispatchedAt}
                  onComplete={handleHiggsfieldComplete}
                  onFailed={handleHiggsfieldFailed}
                />
              ))}
              {activeDismissing.map((d) => {
                const onComplete =
                  d.source === "banan"
                    ? handleBananComplete
                    : d.source === "higgsfield"
                      ? handleHiggsfieldComplete
                      : handleKlingComplete;
                const onFailed =
                  d.source === "banan"
                    ? handleBananFailed
                    : d.source === "higgsfield"
                      ? handleHiggsfieldFailed
                      : handleKlingFailed;
                return (
                  <RunningAgentMiniCard
                    key={d.jobId}
                    jobId={d.jobId}
                    dispatchedAt={d.dispatchedAt}
                    onComplete={onComplete}
                    onFailed={onFailed}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
